import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, OnDestroy, Injector } from '@angular/core';
import { FormStatusEnum, WorkRequired, OperationalApprovalCode, ComponentType, OlogNotificationType, FormType, Fields, Tag } from 'src/app/common/enumerations/enumerations';
import { WfSignature, WfSignatureResource, SaveTaskResource, WFTaskSignatureResource } from 'src/app/components/workflow/workflow';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { OLogService } from 'src/app/components/olog/olog.service';
import { SCFMaster, SaveFormViewStateResource } from '../../scf';
import { SCFPrivileges, ScfV2Service, SCFWorkRequired } from '../../scf-v2.service';
import { FormViewState } from 'src/app/common/models/form-view-state';
import { Observable, Subscription, firstValueFrom, takeUntil } from 'rxjs';
import { KEService } from 'src/app/components/kenable/ke.service';
import { Restriction } from 'src/app/controls/restrictions/services/restriction';
import { OpApprovalValue } from 'src/app/controls/checklist-components/op-approval-w-checklists/op-approval-w-checklists.component';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';

@Component({
  selector: 'scf-v2-main-s5',
  templateUrl: './scf-v2-main-s5.component.html',
  styleUrls: ['./scf-v2-main-s5.component.scss']
})
export class ScfV2MainS5Component extends BaseComponent implements OnChanges, OnDestroy {

  @Input() scfMaster?: SCFMaster | null;
  @Input() disabled? = true;
  @Input() privileges?: SCFPrivileges;
  @Input() status?: FormViewState;

  @Output() changed = new EventEmitter<SCFMaster>();
  @Output() saved = new EventEmitter<SCFMaster>();

  disableControls = true;

  signatures?: WfSignature[];
  wfSignatures?: WfSignatureResource[];
  wfSignCount?: number;

  restrictions!: Restriction[];
  restrictionsFiltered!: Restriction[];
  restrictions$!: Observable<Restriction[]>;
  restrictionsSubs!: Subscription;

  questionDisabled = false;

  message?: string;

  componentType = ComponentType;

  pendingOpApprovalChanges?: OpApprovalValue;

  constructor(
    private scfService: ScfV2Service,
    private wf: WorkFlowService,
    protected override injector: Injector,
    private _olog: OLogService,
    private keService: KEService
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
    this.loadRestrictions();
  }

  loadRestrictions() {
    this.restrictions$ = this.store.select((state) => state.Restrictions.data);
    this.restrictionsSubs = this.restrictions$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data?.length) {
        this.restrictions = data;
      }
    });
  }

  refresh() {
    this.setDisabled();
    this.getValues();
  }

  setDisabled() {
    this.disabled =
      this.scfMaster?.status != FormStatusEnum.WorkCompleted || this.scfMaster?.status != FormStatusEnum.WorkCompleted ||
      !this.getCurrentUser();
  }

  getValues() {
    this.wfSignatures = [];
    this.signatures = [];
    this.disabled = this.disabled && (this.scfMaster?.status == FormStatusEnum.AmendmentDrafting && !this.status?.isViewingAmendment || this.scfMaster?.status != FormStatusEnum.AmendmentDrafting);
    this.message = this.getMessage('OperationApprovalPendingAmendment')?.description;
    if (this.scfMaster) {
      if (this.status?.isViewingAmendment) {
        const scfAmendment = this.scfMaster?.scfAmendments?.find(x => x.active);
        this.wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 4)?.wfSignatures?.filter(x => x.wfTaskLocal.amendmentID == scfAmendment?.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const wfSignaturesOriginal = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 4)?.wfSignatures?.filter(x => x.wfTaskLocal.amendmentID == null).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        this.wfSignatures?.map(s => {
          s.followUpChanged = wfSignaturesOriginal?.find(x => x.code == s.code)?.followUpChanged;
          s.lastFollowUpChanged = wfSignaturesOriginal?.find(x => x.code == s.code)?.lastFollowUpChanged;
          s.lastFollowUpCheckChanged = wfSignaturesOriginal?.find(x => x.code == s.code)?.lastFollowUpCheckChanged;
          s.answerChangedBy = wfSignaturesOriginal?.find(x => x.code == s.code)?.answerChangedBy;
          s.answerChangedById = wfSignaturesOriginal?.find(x => x.code == s.code)?.answerChangedById;
        });
      }
      else {
        this.wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 4)?.wfSignatures?.filter(x => x.wfTaskLocal.amendmentID == null).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      }
      this.createSignatures();
    }
  }

  createSignatures() {
    this.wfSignCount = this.wfSignatures?.filter(x => x.signed).length;
    if (!this.wfSignatures) return;
    for (const t of this.wfSignatures) {
      const signature: WfSignature = {
        id: t.id,
        code: t.code,
        approveActionId: t.approveID,
        unapproveActionId: t.unapproveID,
        disapproveActionId: t.disapproveID,
        amendmentId: t.amendmentID,
        amendmentNumber: t.amendmentNumber,
        name: t.titleBefore,
        roles: t.roles.map(r => r?.id),
        roleCodes: t.roles.sort((r1, r2) => this.utils.sort(r1.code, r2.code)).map(r => r?.code).join('/'),
        // Close eligibility is evaluated dynamically by closeDisabled so signature changes are reflected immediately.
        disabled: !t.enabled || this.disabled || (t.answerChangedById ? true : false),
        approvedBy: t.signedBy,
        approved: t.signed,
        approvedOn: t.signedOn,
        approveAction: t.approveEnabled && t.value,
        unapproveAction: t.unapproveEnabled,
        disapproveAction: t.disapproveEnabled,
        amendment: t.amendmentEnabled,
        required: t.value,
        taskId: t.taskID,
        value: t.value,
        enabled: t.enabled,
        type: (t.type == 9 || (t.titleBefore != 'SCF Complete' && t.titleBefore != 'Final Radiation Survey Complete')) ? 3 : (t.type == 0 || t.titleBefore == 'SCF Complete') ? 23 : t.type, // Older forms have different type values
        textValue: t.text,
        configuration: t.configuration,
        question: t.question,
        answerChangedBy: t.answerChangedBy,
        answerChangedById: t.answerChangedById,
        followUpChanged: t.followUpChanged,
        lastFollowUpChanged: t.lastFollowUpChanged,
        lastFollowUpCheckChanged: t.lastFollowUpCheckChanged,
        pendingChange: t.pendingChange,
        pendingChangeObj: t.pendingChangeObj,
        wfTaskLocal: t.wfTaskLocal,
        status: this.scfMaster?.status,
        checkboxDisabled: !this.hasPrivilege(this.privilegeEnum.SCFOperationalApproval) && !(this.hasPrivilege(this.privilegeEnum.SCFApproveRADSChanges) && t.code == Tag.OP_APPR_RAD_CODE) || t.signed ||
          this.scfMaster?.status == FormStatusEnum.Closed ||
          this.scfMaster?.status == FormStatusEnum.Canceled ||
          this.scfMaster?.status == FormStatusEnum.AmendmentDrafting ||
          this.scfMaster?.status == FormStatusEnum.AmendmentPending,

      };

      this.signatures?.push(signature);
    }
  }

  /**
   * Sync operational-approval signature checks from a selected work-required item.
   * Persist SCF master changes before queuing dependent signature updates so cross-field
   * rules (ACC/BL removal and DOC/RAD requirements) are applied against current state.
   * @param workRequired Work-required entry whose checked state drives signature/master updates.
   */
  async setChecksFromWorkRequired(workRequired: SCFWorkRequired): Promise<void> {
    // Skip processing when required context is unavailable.
    if (!this.signatures?.length || !this.scfMaster) return;

    // Normalize incoming checked value to a strict boolean for task payloads.
    const checked = !!workRequired.checked;

    // Resolve signature id for a given operational approval code.
    const getSigId = (code: OperationalApprovalCode): number | undefined =>
      this.signatures?.find(s => s.code === code)?.id;

    const tasks: SaveTaskResource[] = [];
    // Queue a signature update only when the signature exists in the current list.
    const pushTask = (code: OperationalApprovalCode) => {
      const id = getSigId(code);
      if (id != null) tasks.push({ id, value: checked });
    };

    // DOC and RAD approvals are coupled for these work-required flows.
    const pushDocAndRadIfNeeded = () => {
      pushTask(OperationalApprovalCode.DOCUPREQ);
      pushTask(OperationalApprovalCode.OPRADS);
    };

    switch (workRequired.workRequired) {
      case WorkRequired.PermanentAccRemoval: {
        // Persist master first so dependent rule checks read current values.
        await this.saveSCFMaster();

        pushTask(OperationalApprovalCode.ACREVREQ);

        // only add DOC/RAD when BL removal is NOT checked
        if (!this.scfMaster.permanentBLRemoval) {
          pushDocAndRadIfNeeded();
        }

        if (tasks.length) await this.saveTasks(tasks);
        return;
      }

      case WorkRequired.PermanentBLRemoval: {
        // Persist master first so dependent rule checks read current values.
        await this.saveSCFMaster();

        pushTask(OperationalApprovalCode.BLREVREQ);

        // only add DOC/RAD when ACC removal is NOT checked
        if (!this.scfMaster.permanentAccRemoval) {
          pushDocAndRadIfNeeded();
        }

        if (tasks.length) await this.saveTasks(tasks);
        return;
      }

      case WorkRequired.TocaMovement: {
        // This path updates only master state and does not enqueue signature tasks.
        this.scfMaster.tocaMovement = checked;
        await this.saveSCFMaster();
        return;
      }

      case WorkRequired.AccRFShielding:
      case WorkRequired.BlShielding:
      default:
        return;
    }
  }

  sign(taskId?: number, actionId?: number) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId,
      actionId
    };
    this.scfService.sign(wfTaskSignature).toPromise().then(() => {
    }, error => {
      console.log(error);
      this.alert.defaultError();
    });
  }

  //Check any Operational Approval box
  /**
   * Apply a user-provided signature value and persist the change according to the current form status.
   * In non-draft states, record who changed the answer and refresh values; in draft-like states,
   * persist the value directly and re-enable the signature input.
   * @param e Incoming signature value from the UI event.
   * @param id Signature identifier to locate and persist the target signature.
   */
  check(e: any, id?: number) {
    // Resolve the signature targeted by the incoming id.
    const signature = this.signatures?.find(x => x.id == id);
    if (signature && id) {
      // Keep the local model in sync with the latest UI-provided value.
      signature.value = e;
      // Non-draft flows track answer ownership instead of writing the raw value directly.
      if (this.scfMaster?.status !== FormStatusEnum.Draft && this.scfMaster?.status !== FormStatusEnum.AmendmentDrafting && this.scfMaster?.status != FormStatusEnum.New) {
        const saveTask: SaveTaskResource = {
          id,
          field: 'AnswerChangedById',
          answerChangedById: this.getCurrentUser()?.id
        };
        this.saveTask(saveTask);
        // Refresh derived state after persisting answer-changed metadata.
        this.getValues();
      } else {
        const saveTask: SaveTaskResource = {
          id,
          value: e
        };
        // Ensure the control remains editable after draft-state update.
        signature.disabled = false;
        this.saveTask(saveTask);
      }
    }
  }

  /**
   * Unchecks every currently-checked Operational Approval signature. Persists
   * through SaveTasks only (NOT saveSCFMaster), so it does not PUT the whole
   * master — used by the tester Reset, where saving the half-cleared master would
   * fail with a "system error".
   */
  clearOperationalApprovals() {
    const checked = (this.signatures ?? []).filter(
      (s) => !!s.value && s.id != null
    );
    if (!checked.length) return;
    const tasks: SaveTaskResource[] = checked.map((s) => ({
      id: s.id!,
      value: false,
    }));
    this.saveTasks(tasks);
  }

  async saveSCFMaster() {
    if (this.scfMaster) {
      this.setOpenDocument(FormType.SCF, this.scfMaster.id);
      this.scfMaster.scfHoldPoints = null;
      await this.scfService.update(this.scfMaster).toPromise().then(scfMaster => {
        this.scfMaster = scfMaster;
        this.getValues();
        this.setFormDirty(false);
        this.saved.emit(scfMaster);
      })
        .catch(error => {
          console.log(error);
          this.alert.defaultError();
        });
    }
  }

  async saveTask(saveTask: SaveTaskResource) {
    try {
      const data = await this.wf.save(saveTask).toPromise();
      const signature = this.signatures?.find(x => x.id == saveTask.id);
      if (signature)
        signature.value = data.value;
      if ((this.scfMaster?.status == FormStatusEnum.Draft || this.scfMaster?.status == FormStatusEnum.AmendmentDrafting || this.scfMaster?.status == FormStatusEnum.New) && signature) {
        signature.disabled = false;
      }
    } catch (error) {
      console.log(error);
      this.alert.defaultError();
    }
  }

  /**
   * Persist auto-checked operational approvals and keep both local text representations synchronized.
   * @param saveTasks Workflow task values to persist.
   */
  async saveTasks(saveTasks: SaveTaskResource[]): Promise<void> {
    try {
      saveTasks.map(s => {
        const signature = this.signatures?.find(x => x.id == s.id);
        if (signature) {
          signature.value = s.value;
          // Use the same camel-case payload consumed by operational approval controls.
          signature.text = JSON.stringify({ checked: !!s.value });
          signature.textValue = signature.text;
          s.text = signature.text;
        }
      });
      await firstValueFrom(this.scfService.saveTasks(saveTasks));
    }
    catch (error) {
      console.log(error);
      this.alert.defaultError();
    }
  }

  //Approve or Disapprove pending Operational Approval change
  /**
   * Apply an approve/disapprove action to a signature and refresh form values afterwards.
   * Route operational approvals through the workflow endpoint, and when RAD requirement changes,
   * trigger KE processing for restricted resources linked from the current master state.
   * @param id Signature identifier to approve or disapprove.
   * @param approve True to approve; false to disapprove.
   */
  async approve(id?: number, approve: boolean = false) {
    if (id) {
      // Resolve the targeted signature to determine which approval flow applies.
      const signature = this.signatures?.find(x => x.id == id);
      if (signature?.type == this.componentType.OperationalApproval) {
        // Operational approvals are persisted through workflow service.
        await firstValueFrom(this.wf.approveOpApproval(id, approve));
        // If RAD survey requirement changes, update KEs for impacted restricted resources.
        if (signature.code == Tag.OP_APPR_RAD_CODE) {
          const restrictionIDsInMaster = this.scfMaster?.scfRestrictionsV2?.map(r => r.restrictionID).filter(id => id !== undefined) ?? [];
          // Match current restrictions to master links, then collect valid resource ids.
          const restrictedResourceIDs = this.restrictions
            .filter(r => restrictionIDsInMaster.includes(r.id ?? 0))
            .map(r => r.resourceID)
            .filter((id): id is number => id !== undefined);
          // Refresh KEs only when at least one related resource id is present.
          if (restrictedResourceIDs?.length) {
            this.keService.processKES(restrictedResourceIDs);
          }
        }
      }
      else {
        // Non-operational approvals use answer-based approve/disapprove endpoints.
        if (approve) {
          await this.approveAnswer(id, true);
        }
        else {
          await this.disapproveAnswer(id);
        }
      }
      // Reload latest computed values after persisting approval state.
      this.getValues();
    }
  }

  async approveAnswer(id?: number, e?: any) {
    if (id) {
      const saveTask: SaveTaskResource = {
        id,
        field: 'AnswerChangedById',
        answerChangedById: null,
        value: e
      };
      await this.saveTask(saveTask);
    }
  }
  async disapproveAnswer(id?: number) {
    if (id) {
      const saveTask: SaveTaskResource = {
        id,
        field: 'AnswerChangedById',
        answerChangedById: null
      };
      await this.saveTask(saveTask);
    }
  }

  /**
   * Close the SCF form by logging the close event, updating form status, and signing the workflow task.
   * Proceed with status/sign actions only when the OLOG notification succeeds,
   * and surface a form-state alert after the workflow signature is completed.
   * @param taskId Workflow task identifier used to sign the close action.
   * @param actionId Workflow action identifier associated with the close transition.
   */
  async close(taskId?: number, actionId?: number) {
    // Build the payload that transitions the form into Closed state.
    const FormViewStateResource = {
      id: this.scfMaster?.id,
      status: FormStatusEnum.Closed
    } as SaveFormViewStateResource;
    // Publish close notification to OLOG before applying status/sign side effects.
    const response = await this._olog.postToOlog(FormType.SCF, this.scfMaster?.id ?? 0, OlogNotificationType.Closed);
    if (response?.ok) {
      // Persist form status change only after OLOG acknowledges the close event.
      this.scfService.putStatus(FormViewStateResource).toPromise().then(async () => {
        if (this.scfMaster?.id) {
          // Prepare workflow signature payload for the close transition.
          const wfTaskSignature: WFTaskSignatureResource = {
            taskId,
            actionId
          };
          // Sign workflow task and notify user when close state is fully applied.
          this.scfService.sign(wfTaskSignature).toPromise().then(() => {
            this.alert.message('formStateChanged', [new MessagePlaceholder('{serialNo}', this.scfMaster?.serialNo), new MessagePlaceholder('{status}', 'Closed')]);
          }, error => {
            console.log(error);
            this.alert.defaultError();
          });
        }
      });
    }
  }

  /**
   * Determine whether an approval signature is currently required.
   * Prefer the workflow boolean value and tolerate legacy JSON property casing when that value is unavailable.
   * @param signature Signature whose checked state should be evaluated.
   * @returns True when the signature is checked or has a pending answer change.
   */
  private isSignatureRequired(signature: WfSignature): boolean {
    if (signature.value != null) return !!signature.value || !!signature.answerChangedBy || !!signature.pendingChange;
    if (signature.type != ComponentType.OperationalApproval) return !!signature.answerChangedBy || !!signature.pendingChange;

    try {
      const value = JSON.parse(signature.textValue ?? '');
      return !!(value?.checked ?? value?.Checked) || !!signature.answerChangedBy || !!signature.pendingChange;
    }
    catch {
      return !!signature.answerChangedBy || !!signature.pendingChange;
    }
  }

  /**
   * Disable SCF closure until work is completed and every required approval is signed.
   * @param signature Signature control being rendered.
   * @returns True only when the Close control must be disabled.
   */
  closeDisabled(signature: WfSignature): boolean {
    if (signature.type != ComponentType.CloseButton) return false;

    // Validate every required task in Operational Approval, regardless of its legacy component type.
    const pendingApproval = this.signatures
      ?.filter(s => s.type != ComponentType.CloseButton)
      .some(s => this.isSignatureRequired(s) && !s.approved) ?? false;

    return this.scfMaster?.status != FormStatusEnum.WorkCompleted || pendingApproval;
  }

  diffValue(s: WfSignature) {
    return s.value != this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals.find(x => x.number == 4)?.wfSignatures?.find(x => x.code == s.code && x.wfTaskLocal.amendmentID == null)?.value;
  }

  async textChanged(text: string, wfTaskLocalID?: number) {
    if (this.isFormDirty() && (this.scfMaster?.status == FormStatusEnum.New || this.scfMaster?.status == FormStatusEnum.Draft)) {
      await this.saveSCFMaster();
      this.setFormDirty(false);
    }

    await this.wf.opApprovalTaskChanged(FormType.SCF, text, wfTaskLocalID!, this.scfMaster!.status, PrivilegeEnum.SCFApproveRADSChanges);
  }

  setPendingOpApprovalChanges(e: OpApprovalValue) {
    this.pendingOpApprovalChanges = e;
  }

  async saveOPApprovalChanges() {
    if (this.pendingOpApprovalChanges) {
      const valueString = this.utils.JSONstringify(this.pendingOpApprovalChanges);
      this.setFormDirty(false);
      await this.textChanged(valueString, this.pendingOpApprovalChanges.taskID);
      this.pendingOpApprovalChanges = undefined;
    }
  }

  privilegeToApproveChanges(s: WfSignature): PrivilegeEnum {
    const approveRads = s.code == Tag.OP_APPR_RAD_CODE && this.hasPrivilege(this.privilegeEnum.SCFApproveRADSChanges);
    const approveOther = s.code != Tag.OP_APPR_RAD_CODE && this.hasPrivilege(this.privilegeEnum.RPandRWApproveAnswerSCFS4);
    if (approveRads) return PrivilegeEnum.SCFApproveRADSChanges;
    if (approveOther) return PrivilegeEnum.RPandRWApproveAnswerSCFS4;
    return PrivilegeEnum.None;
  }
}
